import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { vehicleOptions } from "./routeConstants";

/**
 * Route Form Dialog Component
 * 
 * Reusable dialog for creating and editing shared routes.
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog state change handler
 * @param {string} title - Dialog title
 * @param {object} form - Form state
 * @param {function} setForm - Form state setter
 * @param {function} onSubmit - Form submit handler
 * @param {string} submitLabel - Submit button label
 */
export function RouteFormDialog({ 
  open, 
  onOpenChange, 
  title, 
  form, 
  setForm, 
  onSubmit, 
  submitLabel = "Create Route" 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Origin & Destination */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Origin Area</Label>
              <Input
                placeholder="e.g., Andheri East"
                value={form.origin}
                onChange={(e) => setForm({ ...form, origin: e.target.value })}
                required
                className="rounded-none"
                data-testid="route-origin"
              />
            </div>
            <div className="space-y-2">
              <Label>Destination Area</Label>
              <Input
                placeholder="e.g., Powai"
                value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })}
                required
                className="rounded-none"
                data-testid="route-destination"
              />
            </div>
          </div>

          {/* Via Stops */}
          <div className="space-y-2">
            <Label>Via Stops (comma separated)</Label>
            <Input
              placeholder="e.g., Jogeshwari, Goregaon, Malad"
              value={form.via_stops}
              onChange={(e) => setForm({ ...form, via_stops: e.target.value })}
              className="rounded-none"
              data-testid="route-via-stops"
            />
          </div>

          {/* Vehicle Type */}
          <div className="space-y-2">
            <Label>Vehicle Type</Label>
            <div className="grid grid-cols-4 gap-2">
              {vehicleOptions.map((vehicle) => (
                <button
                  key={vehicle.id}
                  type="button"
                  onClick={() => setForm({ ...form, vehicle_type: vehicle.id })}
                  className={`p-3 border-2 flex flex-col items-center gap-1 transition-all ${
                    form.vehicle_type === vehicle.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`vehicle-${vehicle.id}`}
                >
                  <vehicle.icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{vehicle.name.split("/")[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Capacity & Price */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Total Capacity (kg)</Label>
              <Input
                type="number"
                placeholder="e.g., 500"
                value={form.total_capacity}
                onChange={(e) => setForm({ ...form, total_capacity: e.target.value })}
                required
                className="rounded-none"
                data-testid="route-capacity"
              />
            </div>
            <div className="space-y-2">
              <Label>Price per kg (INR)</Label>
              <Input
                type="number"
                placeholder="e.g., 5"
                value={form.price_per_kg}
                onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })}
                required
                className="rounded-none"
                data-testid="route-price"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Departure Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-none">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {form.departure_date ? format(form.departure_date, "PP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.departure_date}
                    onSelect={(date) => setForm({ ...form, departure_date: date })}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Departure Time</Label>
              <Input
                type="time"
                value={form.departure_time}
                onChange={(e) => setForm({ ...form, departure_time: e.target.value })}
                required
                className="rounded-none"
                data-testid="route-time"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full rounded-none" 
            disabled={!form.departure_date}
            data-testid="route-submit"
          >
            {submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
